import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { api, ApiError } from '../../lib/api';
import { reportError } from '../../lib/errorEvents';
import { parseProjectNumberDraft, projectNumberRequestError } from '../../lib/projectNumbers';
import { FieldSheet } from '../../components/FieldSheet';
import { formatInvoiceNumber, formatProjectNumber, Modal } from '../../components/primitives';
import type { ProjectDetailRow, InvoiceRenumberPreview } from './types';

const RENUMBER_STALE_NOTE = 'Linked invoices changed — review again';

function ProjectNumberEditor({
  project,
  onSaved,
  mobile = false,
  openSignal = 0,
}: {
  project: ProjectDetailRow;
  onSaved: (project: ProjectDetailRow) => void;
  /** ≤880px: the number has no place in the header, so the editor renders
      nothing and opens its field sheet from the "···" menu instead (§6.1 C).
      The renumber confirm is the same Modal, rendered as a half sheet. */
  mobile?: boolean;
  /** Mobile only — bump to open the field sheet. */
  openSignal?: number;
}) {
  const [editing, setEditing] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingPreview, setPendingPreview] = useState<InvoiceRenumberPreview | null>(null);
  const [staleNote, setStaleNote] = useState<string | null>(null);

  const open = () => {
    setDraft(project.project_number === null ? '' : String(project.project_number));
    setError(null);
    setEditing(true);
  };
  // Mobile: the "···" menu bumps openSignal instead of clicking a header span.
  useEffect(() => {
    if (!mobile || openSignal <= 0) return;
    setDraft(project.project_number === null ? '' : String(project.project_number));
    setError(null);
    setSheetOpen(true);
    // Only the signal opens the sheet; the seed is read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSignal, mobile]);
  const applyNumber = async (
    projectNumber: number | null,
    confirmed?: InvoiceRenumberPreview,
  ) => {
    const response = await api<{ project: ProjectDetailRow }>(
      `/api/admin/projects/${project.id}`,
      {
        method: 'PATCH',
        json: {
          project_number: projectNumber,
          // The confirm is bound to the exact set the dialog showed — the
          // server 409s with a fresh preview if anything drifted meanwhile.
          ...(confirmed
            ? {
                renumber_invoices: true,
                expected_changes: confirmed.changes.map(({ id, new_number }) => ({ id, new_number })),
              }
            : {}),
        },
      },
    );
    onSaved(response.project);
  };

  const save = async (raw: string = draft) => {
    if (saving) return;
    const parsed = parseProjectNumberDraft(raw);
    if (parsed.error) {
      setError(parsed.error);
      // The mobile sheet closes on Save, so its errors need the app toast.
      if (mobile) reportError(new Error(parsed.error), parsed.error);
      return;
    }
    if (parsed.value === project.project_number) {
      setEditing(false);
      setError(null);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (project.project_number !== null && parsed.value !== null) {
        const preview = await api<InvoiceRenumberPreview>(
          `/api/admin/projects/${project.id}/invoice-renumber-preview?project_number=${parsed.value}`,
        );
        if (preview.changes.length > 0) {
          setPendingPreview(preview);
          setStaleNote(null);
          setEditing(false);
          return;
        }
      }
      await applyNumber(parsed.value);
      setEditing(false);
    } catch (saveError) {
      setError(projectNumberRequestError(saveError, 'Failed to update project number.'));
      if (mobile) reportError(saveError, 'Failed to update project number.');
    } finally {
      setSaving(false);
    }
  };

  const confirmRenumber = async () => {
    if (!pendingPreview || saving) return;
    setSaving(true);
    setError(null);
    try {
      await applyNumber(pendingPreview.new_project_number, pendingPreview);
      setPendingPreview(null);
      setStaleNote(null);
    } catch (saveError) {
      // Drift: nothing was applied, so re-show the dialog against the fresh
      // preview the server sent back rather than reporting a failure.
      const fresh =
        saveError instanceof ApiError && saveError.status === 409
          ? (saveError.body.preview as InvoiceRenumberPreview | undefined)
          : undefined;
      if (fresh) {
        setPendingPreview(fresh);
        setStaleNote(RENUMBER_STALE_NOTE);
      } else {
        setError(projectNumberRequestError(saveError, 'Failed to update project number.'));
      }
    } finally {
      setSaving(false);
    }
  };

  // The renumber confirm is the same dialog on both renders — a centred
  // modal on desktop, a half sheet at ≤880px (Modal's mobile branch).
  const renumberPortal = (pendingPreview: InvoiceRenumberPreview) =>
    createPortal(
      <Modal
        title="Change project number?"
        context="INVOICES"
        width={520}
        mobile="half"
        onClose={() => {
          if (saving) return;
          setPendingPreview(null);
          setStaleNote(null);
          setError(null);
        }}
        hint="Project and invoice numbers update together"
        foot={
          <>
            <button
              className="btn"
              onClick={() => {
                setPendingPreview(null);
                setStaleNote(null);
                setError(null);
              }}
              disabled={saving}
            >
              Cancel
            </button>
            <button className="btn primary" onClick={confirmRenumber} disabled={saving}>
              {saving ? 'Changing…' : 'Change number →'}
            </button>
          </>
        }
      >
        {staleNote && <p className="renumber-stale">{staleNote}</p>}
        <p className="renumber-intro">
          Change project {formatProjectNumber(pendingPreview.old_project_number) ?? '—'} to{' '}
          {formatProjectNumber(pendingPreview.new_project_number)}? These linked invoices will
          be renumbered at the same time.
        </p>
        <div className="renumber-heading">Will change</div>
        <div className="renumber-list">
          {pendingPreview.changes.map((change) => (
            <div className="renumber-row" key={change.id}>
              <span className="mono">{change.old_number}</span>
              <span className="renumber-arrow">→</span>
              <span className="mono renumber-new">{change.new_number}</span>
            </div>
          ))}
        </div>
        {pendingPreview.collisions.length > 0 && (
          <div className="renumber-collisions">
            <div className="renumber-heading collision">Already in use</div>
            {pendingPreview.collisions.map((collision) => (
              <div className="renumber-row" key={collision.id}>
                <span className="mono">{formatInvoiceNumber(collision.invoice_number)}</span>
                <span className="renumber-reason">already used by {collision.label}</span>
              </div>
            ))}
            <p className="renumber-collision-note">
              Invoice numbers don&apos;t have to be unique — you can still make this change.
            </p>
          </div>
        )}
        {pendingPreview.unchanged.length > 0 && (
          <>
            <div className="renumber-heading unchanged">Won&apos;t change</div>
            <div className="renumber-list">
              {pendingPreview.unchanged.map((invoice) => (
                <div className="renumber-row" key={invoice.id}>
                  <span className="mono">{invoice.invoice_number}</span>
                  <span className="renumber-reason">manually numbered</span>
                </div>
              ))}
            </div>
          </>
        )}
        {error && <p className="error-text">{error}</p>}
      </Modal>,
      document.body,
    );

  // ≤880px the number has no header slot: "···" → Change project number
  // opens this field sheet, and the renumber confirm stacks over it (§6.1 C).
  if (mobile) {
    return (
      <>
        {sheetOpen && (
          <FieldSheet
            open
            title="Project number"
            context={`Now ${formatProjectNumber(project.project_number) ?? 'unnumbered'}`}
            kind="number"
            value={draft}
            placeholder="232"
            onSave={(raw) => {
              setDraft(raw);
              void save(raw);
            }}
            onClose={() => setSheetOpen(false)}
          />
        )}
        {pendingPreview && renumberPortal(pendingPreview)}
      </>
    );
  }


  if (!editing) {
    return (
      <>
        <span
          className="editable mono"
          onClick={open}
          title="Edit project number"
        >
          {formatProjectNumber(project.project_number) ?? '—'}
        </span>
        {pendingPreview && renumberPortal(pendingPreview)}
      </>
    );
  }

  return (
    <>
      <input
        className="inline-input mono project-number-input"
        type="number"
        min={1}
        step={1}
        value={draft}
        disabled={saving}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={(event) => {
          if (event.currentTarget.dataset.cancelSave !== 'true') void save();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
          if (event.key === 'Escape') {
            event.currentTarget.dataset.cancelSave = 'true';
            setEditing(false);
            setError(null);
          }
        }}
        autoFocus
      />
      {error && <span className="error-text project-number-error">{error}</span>}
    </>
  );
}

export { ProjectNumberEditor };
